import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import {
  descargarEstadoSkytel,
  getSkytelFunnel,
  type EstadoConteo,
  type FunnelStage,
  type LlamadaBucket,
} from '@lib/services/skytel-funnel.service';

const C = {
  ink: '#13212e',
  muted: '#5f7385',
  faint: '#8497a6',
  line: '#e3eaf0',
  lineSoft: '#eef3f7',
  accent: '#1763a6',
  accentDeep: '#103f6d',
  win: '#1c8c57',
  warm: '#bd7f16',
  lose: '#bd483b',
  grey: '#b9c6d1',
};

const nf = new Intl.NumberFormat('es-AR');

function colorConversion(key: string): string {
  if (key === 'ganado') return C.win;
  if (key === 'en_oferta') return C.accent;
  if (key.startsWith('perdido')) return C.lose;
  return C.faint;
}
function colorProceso(key: string): string {
  if (key === 'sin_trabajar') return C.grey;
  if (key === 'en_gestion' || key === 'callback_agendado') return C.warm;
  if (key === 'cerrado') return C.muted;
  return C.accent;
}

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

/**
 * Funnel de la campaña SkyTel (Upgrade MS2): recorrido de la cohorte del
 * contacto al pago, resultado/etapa que reporta SkyTel, e insistencia de
 * llamadas. Cada estado descarga el Excel de esos legajos. Reemplaza Supervisión
 * TMK. Gateada por `view:skytel-funnel`.
 */
export function SkytelFunnelPage() {
  const query = useQuery({ queryKey: ['skytel-funnel'], queryFn: getSkytelFunnel });
  const [bajando, setBajando] = useState<string | null>(null);
  const [errorDl, setErrorDl] = useState<string | null>(null);

  async function descargar(dimension: string, valor: string, k: string) {
    setBajando(k);
    setErrorDl(null);
    try {
      await descargarEstadoSkytel(dimension, valor);
    } catch (e) {
      setErrorDl((e as Error).message || 'No se pudo descargar.');
    } finally {
      setBajando(null);
    }
  }

  const Dl = ({ dimension, valor, k }: { dimension: string; valor: string; k: string }) => (
    <button
      type="button"
      className="sk-dl"
      disabled={bajando === k}
      onClick={() => descargar(dimension, valor, k)}
      title="Descargar Excel de estos legajos"
      aria-label={`Descargar ${valor}`}
    >
      {bajando === k ? <span className="sk-spin" /> : <DownloadIcon />}
    </button>
  );

  const d = query.data;

  return (
    <section>
      <style>{`
        .sk-dl { width:32px; height:32px; border-radius:8px; border:1px solid ${C.line}; background:#fff; color:${C.muted}; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; transition:all .14s ease; padding:0; }
        .sk-dl:hover:not(:disabled) { border-color:${C.accent}; color:${C.accent}; background:#e8f1f9; }
        .sk-dl:disabled { cursor:default; opacity:.6; }
        .sk-dl:focus-visible { outline:2px solid ${C.accent}; outline-offset:2px; }
        .sk-spin { width:13px; height:13px; border:2px solid ${C.line}; border-top-color:${C.accent}; border-radius:50%; animation:sk-rot .7s linear infinite; }
        @keyframes sk-rot { to { transform:rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .sk-spin { animation:none; } }
        .sk-card { background:#fff; border:1px solid ${C.line}; border-radius:12px; padding:22px 24px; margin-top:20px; }
        .sk-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-top:20px; }
        @media (max-width:760px){ .sk-grid { grid-template-columns:1fr; } }
      `}</style>

      <header className="pageHeader">
        <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Funnel SkyTel
        </h1>
        <p className="text-element-mid">
          Cómo avanza la cohorte cedida a SkyTel (campaña Upgrade MS2), del contacto al pago. Cada
          estado descarga el Excel de esos legajos.
        </p>
      </header>

      {query.isLoading && <div style={{ marginTop: 20 }}><TableSkeleton rows={8} /></div>}
      {query.isError && <p className="status status-error" style={{ marginTop: 16 }}>No se pudo cargar el funnel.</p>}
      {errorDl && <p className="status status-error" style={{ marginTop: 12 }}>{errorDl}</p>}

      {d && (
        <>
          {/* ---- FUNNEL ---- */}
          <div className="sk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 680, margin: 0 }}>El recorrido</h2>
              <span style={{ fontSize: 12.5, color: C.faint }}>{nf.format(d.cohorte)} cedidos · % sobre cohorte</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {d.funnel.map((s: FunnelStage) => {
                const col = s.key === 'pago' ? C.win : s.key === 'ganado' ? C.win : C.accent;
                return (
                  <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 120px 36px', alignItems: 'center', gap: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ height: 32, background: C.lineSoft, borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(s.pct, 0.4)}%`, minWidth: 3, borderRadius: 8, background: col }} />
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{nf.format(s.n)}</div>
                      <div style={{ fontSize: 12, color: s.key === 'pago' ? C.win : C.muted, fontVariantNumeric: 'tabular-nums' }}>{s.pct}%</div>
                    </div>
                    <Dl dimension="funnel" valor={s.key} k={`f-${s.key}`} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ---- ATRIBUCIÓN ---- */}
          {d.atribucion.pagoMedicus !== d.atribucion.ganadoSkytel && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#fbfdff', border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.warm}`, borderRadius: 10, padding: '14px 16px', marginTop: 18 }}>
              <span style={{ width: 28, height: 28, borderRadius: 8, background: '#f8efdb', color: C.warm, display: 'grid', placeItems: 'center', fontWeight: 800, flex: '0 0 auto' }}>≠</span>
              <p style={{ margin: 0, fontSize: 13.5, color: C.muted }}>
                <b style={{ color: C.ink }}>Atribución:</b> SkyTel reporta <b style={{ color: C.ink }}>{nf.format(d.atribucion.ganadoSkytel)} “ganado”</b>, pero en Medicus hay <b style={{ color: C.ink }}>{nf.format(d.atribucion.pagoMedicus)} pagos aprobados</b> en la cohorte — SkyTel subcuenta las conversiones reales.
              </p>
            </div>
          )}

          {/* ---- LLAMADAS ---- */}
          <div className="sk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 680, margin: 0 }}>Llamadas</h2>
              <span style={{ fontSize: 12.5, color: C.faint }}>quiénes recibieron llamado y con qué insistencia</span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { n: d.llamadas.legajosLlamados, l: 'legajos llamados' },
                { n: d.llamadas.totalLlamadas, l: 'llamadas totales' },
                { n: d.llamadas.promedio, l: 'promedio por legajo' },
                { n: d.llamadas.nuncaLlamados, l: 'nunca llamados', dim: true },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, minWidth: 130, background: '#fbfdfe', border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 22, fontWeight: 720, color: s.dim ? C.faint : C.ink, fontVariantNumeric: 'tabular-nums' }}>{nf.format(s.n)}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{s.l}</div>
                </div>
              ))}
            </div>
            <Barras data={d.llamadas.buckets.map((b: LlamadaBucket) => ({ key: b.key, label: b.label, n: b.legajos, color: b.key === '11+' ? C.lose : C.accent }))} dimension="llamadas" Dl={Dl} sufijo="legajos" />
          </div>

          {/* ---- RESULTADO + ETAPA ---- */}
          <div className="sk-grid">
            <div className="sk-card" style={{ marginTop: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <h2 style={{ fontSize: 15, fontWeight: 680, margin: 0 }}>Resultado</h2>
                <span style={{ fontSize: 12.5, color: C.faint }}>lo que reporta SkyTel</span>
              </div>
              <Barras data={d.conversion.map((s: EstadoConteo) => ({ key: s.key, label: s.label, n: s.n, color: colorConversion(s.key) }))} dimension="conversion" Dl={Dl} />
            </div>

            <div className="sk-card" style={{ marginTop: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <h2 style={{ fontSize: 15, fontWeight: 680, margin: 0 }}>Etapa actual</h2>
                <span style={{ fontSize: 12.5, color: C.faint }}>dónde está cada uno hoy</span>
              </div>
              <Barras data={d.proceso.map((s: EstadoConteo) => ({ key: s.key, label: s.label, n: s.n, color: colorProceso(s.key) }))} dimension="proceso" Dl={Dl} />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

interface BarraItem { key: string; label: string; n: number; color: string }
function Barras({
  data,
  dimension,
  Dl,
  sufijo,
}: {
  data: BarraItem[];
  dimension: string;
  Dl: (p: { dimension: string; valor: string; k: string }) => React.ReactElement;
  sufijo?: string;
}) {
  const max = Math.max(1, ...data.map((x) => x.n));
  return (
    <div>
      {data.map((x) => (
        <div key={x.key} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 32px', alignItems: 'center', gap: 12, padding: '9px 0', borderTop: `1px solid ${C.lineSoft}` }}>
          <div style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: x.color, flex: '0 0 auto' }} />
            {x.label}
          </div>
          <div>
            <div style={{ textAlign: 'right', fontSize: 15, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
              {new Intl.NumberFormat('es-AR').format(x.n)} {sufijo && <span style={{ fontSize: 11, color: C.faint, fontWeight: 400 }}>{sufijo}</span>}
            </div>
            <div style={{ height: 7, background: C.lineSoft, borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
              <div style={{ height: '100%', width: `${(x.n / max) * 100}%`, borderRadius: 4, background: x.color }} />
            </div>
          </div>
          <Dl dimension={dimension} valor={x.key} k={`${dimension}-${x.key}`} />
        </div>
      ))}
    </div>
  );
}
